import axios from 'axios';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { apiBaseUrl } from '../../../Component/connection';

export default function PaymentReviewLogin() {
  const {orderSeq = ''} = useParams();
  const validOrder = /^[1-9]\d*$/.test(orderSeq) && Number.isSafeInteger(Number(orderSeq));
  const [userid, setUserid] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const alive = useRef(true);
  const activeOrder = useRef(orderSeq);
  activeOrder.current = orderSeq;
  useEffect(() => {alive.current = true; return () => {alive.current = false;};}, []);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (lock.current || !validOrder || !userid.trim() || !password) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const result = await axios.post(`${apiBaseUrl()}user/login`, {userid: userid.trim(), userpw: password}, {withCredentials: true, timeout: 20000});
      if (!alive.current || activeOrder.current !== orderSeq) return;
      if (!result.data?.accessToken) {setError('로그인 결과를 확인하지 못했습니다. 다시 시도해 주세요.'); return;}
      // Legacy stored sessions take precedence over cookies in the shared refresh flow.
      for (const storage of [localStorage, sessionStorage]) {storage.removeItem('key'); storage.removeItem('authority');}
      setPassword('');
      window.location.replace(`/bill/${orderSeq}`);
    } catch (error) {
      if (!alive.current || activeOrder.current !== orderSeq) return;
      const status = (error as {response?: {status?: number}}).response?.status;
      setError(status === 401 || status === 403 ? '아이디와 비밀번호 또는 계정 이용 상태를 확인해 주세요.' : !status ? '서버에 연결하지 못했습니다. 네트워크 상태를 확인하고 다시 시도해 주세요.' : '로그인을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {lock.current = false; if (alive.current) setBusy(false);}
  };
  return <Page><main>
    <img src={require('../../../Image/Logo_Text.png')} alt="팬버드" />
    <h1>결제 심사 정산서</h1>
    {validOrder ? <>
      <p>안내받은 구매자 계정으로 로그인하면 정산서 #{orderSeq}을 확인할 수 있습니다.</p>
      <form onSubmit={submit}>
        <label htmlFor="payment-review-id">구매자 아이디</label>
        <input id="payment-review-id" value={userid} onChange={event => setUserid(event.target.value)} autoComplete="username" required disabled={busy} maxLength={100} />
        <label htmlFor="payment-review-password">비밀번호</label>
        <input id="payment-review-password" type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" required disabled={busy} maxLength={255} />
        {error && <p className="error" role="alert">{error}</p>}
        <button type="submit" disabled={busy || !userid.trim() || !password}>{busy ? '로그인 중…' : '로그인하고 정산서 열기'}</button>
      </form>
      <p className="payment-notice">심사용 주문입니다. 운영 결제창에서 결제를 완료하면 실제 금액이 청구됩니다. 로그인만으로 결제되지는 않습니다.</p>
    </> : <p role="alert">정산서 주소가 올바르지 않습니다. 전달받은 링크를 다시 확인해 주세요.</p>}
    <a href="/">팬버드 홈으로</a>
  </main></Page>;
}

const Page = styled.div`
  min-height: 100dvh; padding: 64px 20px; display: flex; justify-content: center; align-items: flex-start;
  background: var(--user-surface-muted); color: var(--user-text); font-family: 'Pretendard-Regular', sans-serif;
  &, * { box-sizing: border-box; }
  main { width: 440px; max-width: 100%; background: var(--user-surface); padding: 36px; border: 1px solid var(--user-border); border-radius: 12px; }
  img { display: block; width: 154px; max-width: 100%; height: auto; margin-bottom: 30px; }
  h1 { margin: 0 0 12px; font-size: 24px; line-height: 1.4; font-family: 'Pretendard-SemiBold', sans-serif; }
  p { margin: 0; font-size: 15px; line-height: 1.7; color: var(--user-text-muted); overflow-wrap: anywhere; }
  form { display: flex; flex-direction: column; margin-top: 28px; }
  label { margin-bottom: 8px; font-size: 14px; }
  input { width: 100%; min-height: 48px; padding: 12px; border: 1px solid var(--user-border); border-radius: 6px; font: inherit; font-size: 16px; color: var(--user-text); background: var(--user-surface); caret-color: #d92661; }
  input + label { margin-top: 20px; }
  button { min-height: 48px; margin-top: 24px; border: 0; border-radius: 6px; background: #d92661; color: #fff; font: inherit; cursor: pointer; }
  button:hover:not(:disabled) { background: #b91d50; }
  button:disabled { opacity: .55; cursor: not-allowed; }
  input:disabled { background: var(--user-surface-muted); }
  input:focus-visible, button:focus-visible, a:focus-visible { outline: 3px solid #d92661; outline-offset: 3px; }
  .error { margin-top: 16px; color: var(--user-error); }
  .payment-notice { margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--user-border); font-size: 14px; }
  a { display: inline-block; margin-top: 24px; color: var(--user-text-muted); font-size: 14px; text-underline-offset: 4px; }
  ::selection { background: var(--user-accent-soft); color: var(--user-text); }
  @media (max-width: 480px) { padding: 24px 16px; main { padding: 28px 24px; } }
`;
